<script setup>
import {computed, ref} from 'vue'
import store from '../../store'
import helpers from '@/lib/helpers'

const props = defineProps({
  line: Object,
  url: String
})

const record = ref(props.line || null)

const refresh = async () => {
  if (props.url)
    record.value = await helpers.asyncAction(props.url, 'GET')
}

refresh()

const condition_text = computed(() => {
  const opt = (store.state.settings.defaults.inventory_condition || []).find(o => o.value === record.value.condition)
  return opt ? opt.text : record.value.condition
})

const has_appraisal = computed(() => record.value.research?.status === 'complete' && !!record.value.research?.appraisal)
const appraisal = computed(() => record.value.research?.appraisal)

const has_ship_info = computed(() => {
  const s = record.value.ship_info
  return s?.status === 'success' && (s?.dimensions_in?.length || s?.weight_lb)
})

const dims_str = computed(() => {
  const d = record.value.ship_info?.dimensions_in
  return d?.length ? `${d.length?.toFixed(1)}" x ${d.width?.toFixed(1)}" x ${d.height?.toFixed(1)}"` : null
})
</script>

<template>
  <modal :title="'Bid Line Details' + (record ? ' - ' + record.gli : '')" large height="600px" no-scroll>
    <b-tabs card v-if="record">
      <b-tab title="Details" active>
        <b-container fluid class="py-2">
          <b-row>
            <b-col md="6">
              <h6 class="text-muted mb-2">Identification</h6>
              <dl class="row mb-4">
                <dt class="col-5">GLI</dt>
                <dd class="col-7">{{ record.gli }}</dd>

                <dt class="col-5">Bid GLI</dt>
                <dd class="col-7"><b-link @click="global_lookup(record.bid_gli, true)">{{ record.bid_gli }}</b-link></dd>

                <dt class="col-5">Label</dt>
                <dd class="col-7">{{ record.label || '-' }}</dd>

                <dt class="col-5">Manuf</dt>
                <dd class="col-7">{{ record.manuf || '-' }}</dd>

                <dt class="col-5">Description</dt>
                <dd class="col-7">{{ record.description || '-' }}</dd>

                <dt class="col-5">Condition</dt>
                <dd class="col-7">{{ condition_text || '-' }}</dd>

                <dt class="col-5">Qty</dt>
                <dd class="col-7">{{ record.qty ?? '-' }}</dd>

                <dt class="col-5">Reference</dt>
                <dd class="col-7">
                  <b-link v-if="record.reference" :href="record.reference" target="_blank">Link</b-link>
                  <template v-else>-</template>
                </dd>

                <dt class="col-5">More Info</dt>
                <dd class="col-7">{{ record.more_info || '-' }}</dd>
              </dl>
            </b-col>

            <b-col md="6">
              <h6 class="text-muted mb-2">Value &amp; Offer</h6>
              <dl class="row mb-4">
                <dt class="col-5">Est Value</dt>
                <dd class="col-7">
                  <template v-if="record.est_value_low === null && record.est_value === null">-</template>
                  <template v-else>{{ record.est_value_low | money_short }} to {{ record.est_value | money_short }}</template>
                  <b-badge v-if="record.valueby_user" class="ml-1" size="sm">{{ record.valueby_user.userid }}</b-badge>
                </dd>

                <dt class="col-5">Offer</dt>
                <dd class="col-7">
                  <template v-if="record.offer_low === null && record.offer === null">-</template>
                  <template v-else>{{ record.offer_low | money_short }} to {{ record.offer | money_short }}</template>
                  <b-badge v-if="record.offerby_user" class="ml-1" size="sm">{{ record.offerby_user.userid }}</b-badge>
                </dd>

                <dt class="col-5">Est Ship Cost</dt>
                <dd class="col-7">
                  <template v-if="record.est_ship_cost">{{ record.est_ship_cost | money_short }}</template>
                  <template v-else>-</template>
                </dd>

                <dt class="col-5">Confirmed</dt>
                <dd class="col-7">
                  <b-badge variant="success" v-if="record.confirmed">
                    {{ record.confirmed }}<template v-if="record.confirmed_by_user"> by {{ record.confirmed_by_user.name }}</template>
                  </b-badge>
                  <template v-else>-</template>
                </dd>
              </dl>
            </b-col>
          </b-row>

          <b-row v-if="has_appraisal">
            <b-col cols="12">
              <h6 class="text-muted mb-2">AI Appraisal</h6>
              <p class="mb-1">
                {{ appraisal.value_low | money_short }}
                <template v-if="appraisal.value_low !== appraisal.value"> to {{ appraisal.value | money_short }}</template>
                <b-badge
                    class="ml-2"
                    :variant="(appraisal.confidence >= 8 && 'success') || (appraisal.confidence > 4 && 'warning') || 'danger'"
                >
                  Score {{ appraisal.confidence }}
                </b-badge>
                <b-badge class="ml-1" variant="secondary" v-if="appraisal.no_sold_estimate">No Match</b-badge>
              </p>
              <p class="small text-muted mb-0" v-if="appraisal.reason">{{ appraisal.reason }}</p>
            </b-col>
          </b-row>

          <b-row v-if="has_ship_info" class="mt-3">
            <b-col cols="12">
              <h6 class="text-muted mb-2">Shipping Estimate</h6>
              <p class="mb-0">
                <span v-if="dims_str">{{ dims_str }}</span>
                <span v-if="dims_str && record.ship_info.weight_lb" class="mx-2">&middot;</span>
                <span v-if="record.ship_info.weight_lb">{{ record.ship_info.weight_lb.toFixed(1) }} lbs</span>
              </p>
            </b-col>
          </b-row>
        </b-container>
      </b-tab>

      <b-tab title="Images">
        <photo-gallery
            :readonly="!can('bids.lines.files.write')"
            not-photomap
            with-filename
            with-meta
            :photos="record.photos"
            :uploadUrl="record._links.photo_url.href"
            :width="200"
        />
      </b-tab>

      <b-tab title="Files">
        <files standalone :url="record._links.file_url.href" height="400px"/>
      </b-tab>
    </b-tabs>

    <template #footer>
      <b-btn size="sm" variant="outline-secondary" class="px-3" @click="$root.$emit('submit')">Close</b-btn>
    </template>
  </modal>
</template>

<style scoped>
dt {
  font-weight: 500;
}
</style>
